export type ArticleType = 'tutorial' | 'blog';

export interface ArticleSection {
  heading: string;
  body: string;
}

export interface ArticleContent {
  type: ArticleType;
  slug: string;
  title: string;
  summary: string;
  published: string;
  readingTime: string;
  heroImage?: string;
  tags: string[];
  sections: ArticleSection[];
  tips?: string[];
  checklist?: string[];
}

export const tutorialArticles: ArticleContent[] = [
  {
    type: 'tutorial',
    slug: 'instagram-reels-tips',
    title: 'Instagram Reels Tips for Faster Growth',
    summary: 'Optimize hooks, pacing, and captions to help every Reel ship faster and perform better.',
    published: '2025-09-29',
    readingTime: '7 min read',
    heroImage: '/images/tutorials/instagram-reels-tips.jpg',
    tags: ['Instagram Reels', 'Short-form video', 'Growth'],
    sections: [
      {
        heading: 'Open With A Pattern Interrupt',
        body: 'Use a face-forward close up or bold typography in the first 0.7 seconds. ClipThis can automatically detect speech and camera jumps, so select a segment that starts mid-action instead of fading in from black.',
      },
      {
        heading: 'Stack Captions and Overlays',
        body: 'Reels still autoplay without sound. Enable caption rendering directly in ClipThis, then add a secondary text overlay that highlights the payoff. Keep the overlay under three words so it does not compete with subtitles.',
      },
      {
        heading: 'Post-Collapse CTA',
        body: 'Use the final 1.5 seconds for a kinetic CTA. The multi-signal highlights inside ClipThis help you find emotional peaks that convert. Export to 1080x1920 at 12–15 Mbps for crisp uploads.',
      },
    ],
    tips: [
      'Use a 9:16 safe margin template to protect text from UI chrome.',
      'Duplicate strong Reels into TikTok and Shorts with the cross-platform exporter.',
      'Batch three hooks per topic and A/B test thumbnails.',
    ],
  },
  {
    type: 'tutorial',
    slug: 'youtube-shorts-guide',
    title: 'Complete Guide to YouTube Shorts',
    summary: 'Structure Shorts for retention loops using ClipThis AI segments.',
    published: '2025-09-29',
    readingTime: '6 min read',
    heroImage: '/images/tutorials/youtube-shorts-guide.jpg',
    tags: ['YouTube Shorts', 'Monetization'],
    sections: [
      {
        heading: 'Use Jump Cuts to Reset Attention',
        body: 'Shorts favor rapid context switches. Combine ClipThis scene-change tags with the timeline editor to create an intentional jump every 1.8–2.2 seconds.',
      },
      {
        heading: 'Loop the Ending to the Start',
        body: 'Place a micro-hook in both the first and last second. Shorts loops automatically: if your last line mirrors the opening question, retention skyrockets.',
      },
      {
        heading: 'Metadata Workflow',
        body: 'Upload via desktop for faster processing. Pair a title with the format “Hook + Outcome + Emoji”, keep descriptions <150 characters, and add 3 hashtags.',
      },
    ],
    tips: [
      'Aim for 45–55 seconds: long enough for ads but short enough to loop.',
      'Use ClipThis vertical safe zone overlay.',
      'Schedule Shorts in Creator Studio for consistent timing.',
    ],
  },
  {
    type: 'tutorial',
    slug: 'ai-highlight-detection',
    title: 'AI Highlight Detection 101',
    summary: 'Understand how ClipThis scores audio, sentiment, and visuals so you can trust the automated cuts.',
    published: '2025-09-29',
    readingTime: '5 min read',
    tags: ['AI Editing', 'Workflow'],
    sections: [
      {
        heading: 'Signal Stack',
        body: 'ClipThis combines audio spikes, speech keywords, sentiment, and scene changes. Each signal becomes a tag on the timeline so you can filter by moment type.',
      },
      {
        heading: 'Confidence Scores',
        body: 'Every clip receives a 0–100 confidence score. Anything above 72 typically contains a meme-worthy reaction, while 55–70 clips make excellent B-roll or cutaways.',
      },
      {
        heading: 'Manual Overrides',
        body: 'Use the Timing Editor to adjust the in/out points while keeping the AI tags intact. This preserves searchable metadata for future exports.',
      },
    ],
    tips: [
      'Lock clips before exporting so the AI service does not regenerate them.',
      'Combine “speech_highlight” with “facial_emotion” to find riveting storytelling moments.',
    ],
  },
  {
    type: 'tutorial',
    slug: 'how-to-create-viral-clips',
    title: 'How to Create Viral Clips in One Sitting',
    summary: 'A repeatable process for going from long-form recording to polished clips in under 45 minutes.',
    published: '2025-09-29',
    readingTime: '8 min read',
    tags: ['Workflow', 'Creator Ops'],
    sections: [
      {
        heading: 'Prep Your Footage',
        body: 'Upload the highest-bitrate recording you have. ClipThis normalizes audio automatically, but the cleaner the source, the better the transcript and tags.',
      },
      {
        heading: 'Three-Layer Review',
        body: 'First skim the timeline for multi-signal highlights. Next, scan the transcript for quotable hooks. Finally, preview the AI-selected clips and mark keepers.',
      },
      {
        heading: 'Export Variations',
        body: 'Export at least two aspect ratios per clip (9:16 + 16:9). Swap color palettes per network in the exporter so each version feels native.',
      },
    ],
    tips: [
      'Use the guest-share feature to gather feedback before publishing.',
      'Keep a notion doc of viral openers you can reuse.',
    ],
  },
  {
    type: 'tutorial',
    slug: 'tiktok-optimization',
    title: 'TikTok Optimization Playbook',
    summary: 'Leverage ClipThis templates and scheduling tips to stay on the right side of TikTok’s algorithm shifts.',
    published: '2025-09-29',
    readingTime: '6 min read',
    tags: ['TikTok', 'Distribution'],
    sections: [
      {
        heading: 'Dynamic Captions',
        body: 'TikTok rewards accessibility. Export clips with animated captions and highlight keywords in red to mirror TikTok’s native style.',
      },
      {
        heading: 'Posting Cadence',
        body: 'Ship one clip daily for two weeks, then evaluate retention. Use ClipThis analytics to tag top-performing hooks and double down on similar topics.',
      },
      {
        heading: 'Community Signals',
        body: 'Reply to comments with new clips. ClipThis keeps your source timestamps so you can quickly render a follow-up based on viewer questions.',
      },
    ],
    tips: [
      'Keep descriptions under 220 characters.',
      'Use 2–3 niche hashtags plus one trend hashtag.',
      'Enable TikTok’s “Series” feature for binge-friendly playlists.',
    ],
  },
];

export const blogArticles: ArticleContent[] = [
  {
    type: 'blog',
    slug: 'how-to-go-viral-on-tiktok',
    title: 'How to Go Viral on TikTok in 2025',
    summary: 'Practical signals TikTok cares about this year, based on aggregated creator analytics inside ClipThis.',
    published: '2025-09-29',
    readingTime: '9 min read',
    tags: ['TikTok', 'Strategy'],
    sections: [
      {
        heading: 'Watch Time Is Still #1',
        body: 'We analyzed 12K TikTok uploads and found total watch time (not completion rate) correlates strongest with For You placement. Craft clips that spike interest every 2–3 seconds.',
      },
      {
        heading: 'Native Editing Wins',
        body: 'TikTok quietly boosts clips that use its text or sticker layers. Export a clean version from ClipThis, then add one native sticker before posting to check the box.',
      },
      {
        heading: 'Reply Clips Outperform Duets',
        body: 'In 2025, reply videos average 18% more watch time than duets. Use ClipThis to quickly trim a mini-response and include the original comment screenshot in the first frame.',
      },
    ],
    checklist: [
      'Hook within 0.5 seconds.',
      'Keep energy spikes every 2 seconds.',
      'Reply to comments with additional clips.',
    ],
  },
  {
    type: 'blog',
    slug: 'youtube-shorts-algorithm-2025',
    title: 'YouTube Shorts Algorithm Breakdown (2025 Edition)',
    summary: 'Understand how viewer history, sound selection, and loop intent affect Shorts velocity this year.',
    published: '2025-09-29',
    readingTime: '7 min read',
    tags: ['YouTube', 'Algorithm'],
    sections: [
      {
        heading: 'Viewer Graphs',
        body: 'Shorts now considers the long-form videos a viewer recently watched. Reference those topics in your captions and tags for a higher relevancy score.',
      },
      {
        heading: 'Sound Selection',
        body: 'Use ClipThis’ rights-cleared music packs or pick trending sounds within YouTube. Non-trending audio needs a stronger hook to compensate.',
      },
      {
        heading: 'Loop Intent',
        body: 'Shorts that loop seamlessly signal to YouTube that they can be shown again. Write scripts that end on a question or callback to the opening shot.',
      },
    ],
    checklist: [
      'Publish Shorts within 24 hours of the long-form drop.',
      'Use high-contrast captions.',
      'Always include #shorts plus a niche hashtag.',
    ],
  },
  {
    type: 'blog',
    slug: 'best-ai-video-editing-tools-2025',
    title: 'Best AI Video Editing Tools in 2025',
    summary: 'A practical comparison of ClipThis and other AI-first editors with notes on where each shines.',
    published: '2025-09-29',
    readingTime: '10 min read',
    tags: ['AI Editing', 'Tooling'],
    sections: [
      {
        heading: 'Automatic Highlight Detection',
        body: 'ClipThis, Descript, and OpusClip all detect highlights, but ClipThis is the only tool that maps combined audio + facial sentiment in a single timeline view.',
      },
      {
        heading: 'Template Flexibility',
        body: 'We benchmarked export templates, aspect ratios, and caption styles. ClipThis ships with 30+ presets and a CSS-based editor so you can fine-tune brand kits.',
      },
      {
        heading: 'Collaboration',
        body: 'AI tools shine when teams can comment on clips. ClipThis supports guest review links with timestamped notes, while most competitors still require account seats.',
      },
    ],
    checklist: [
      'Map your workflow requirements before testing tools.',
      'Use ClipThis for highlight detection and platform-ready exports.',
      'Pair with a dedicated color-grading tool for cinematic edits.',
    ],
  },
];

